import type {ReactElement} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {SURVEY_FIELD_ERROR_ID_PREFIX} from '@Components/ai-chat-window/components/survey/survey.types';
import type {MultiSelectFieldRendererProps} from './fields.types';
import {OptionCard} from './option-card';
import styles from './fields.module.scss';

function toggleValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}

function buildValidator(field: MultiSelectFieldRendererProps['field'], required: boolean): (value: unknown) => true | string {
  return (value: unknown): true | string => {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    if (required && arr.length === 0) {
      return window.i18next.t('pmwjs_required');
    }
    if (typeof field.min === 'number' && arr.length < field.min) {
      return window.i18next.t('pmwjs_survey_select_at_least_x', {count: field.min});
    }
    if (typeof field.max === 'number' && arr.length > field.max) {
      return window.i18next.t('pmwjs_survey_select_at_most_x', {count: field.max});
    }
    return true;
  };
}

export function MultiSelectFieldRenderer({name, step, field}: MultiSelectFieldRendererProps): ReactElement {
  const {control} = useFormContext();
  const required = step.required !== false;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[]}
      rules={{validate: buildValidator(field, required)}}
      render={({field: rhfField, fieldState}): ReactElement => {
        const current: string[] = Array.isArray(rhfField.value) ? (rhfField.value as string[]) : [];
        return (
          <div
            className={styles.optionList}
            role="group"
            aria-label={step.helpText}
            aria-invalid={fieldState.invalid || undefined}
            aria-describedby={fieldState.invalid ? `${SURVEY_FIELD_ERROR_ID_PREFIX}${name}` : undefined}
          >
            {field.options.map((opt) => {
              const isChecked = current.includes(opt.value);
              return (
                <OptionCard
                  key={opt.value}
                  label={opt.label}
                  imageUrl={opt.imageUrl}
                  isSelected={isChecked}
                  leadingSlot={<Checkbox isChecked={isChecked} type={CHECKBOX_TYPE.PRIMARY} />}
                  onClick={(): void => {
                    rhfField.onChange(toggleValue(current, opt.value));
                  }}
                />
              );
            })}
          </div>
        );
      }}
    />
  );
}
